'use client';

import { useBunja } from 'bunja/react';

import type { GetDefiYieldDetailsResponse } from '@flipster/apis/v1/dynamic-earn/types';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { AutomationIconography } from '@flipster/react/graphic-assets/iconography/AutomationIconography';
import { InterfaceIconography } from '@flipster/react/graphic-assets/iconography/InterfaceIconography';
import { SeamlessIconography } from '@flipster/react/graphic-assets/iconography/SeamlessIconography';
import { NrsusdtpCurrency } from '@flipster/utils/constants/currency';

import { DetailsPageLayout } from '../../shared/details-page/layout';
import { ProductConfigProvider, type AdvantageItem, type FaqItem } from '../../shared/details-page/product-config';
import { nrsUsdtpPricePrecision } from '../../shared/precision';
import { narsilDetailsBunja, narsilOverviewBunja } from '../../shared/state';
import { useNrsusdtpSubscribeModal } from '../../shared/subscribe-redeem-flow/subscribe-modal';

const getNarsilFaqItems = (details: GetDefiYieldDetailsResponse | undefined): FaqItem[] => [
  {
    title: <FormattedMessage id='Earn.r260601.What_narsil_usdt_prime_faq_title' />,
    description: <FormattedMessage id='Earn.r260601.Narsil_usdt_prime_defi_yield_product_designed_faq_answer' />,
  },
  {
    title: <FormattedMessage id='Earn.r251204.What_are_risks_faq_title' />,
    description: <FormattedMessage id='Earn.r260601.Narsil_usdt_prime_market_volatility_risk_faq_answer' />,
  },
  {
    title: <FormattedMessage id='Earn.r251204.Where_rewards_come_from_faq_title' />,
    description: <FormattedMessage id='Earn.r260601.Rewards_narsil_labs_on_chain_stablecoin_faq_answer' />,
  },
  {
    title: <FormattedMessage id='Earn.r251209.How_long_subscription_redemption_faq_title' />,
    description: <FormattedMessage id='Earn.r260113.Subscriptions_processed_hours_redemptions_instantly_faq_answer' />,
  },
  {
    title: <FormattedMessage id='Earn.r251209.Does_flipster_charge_fees_faq_title' />,
    description: (
      <FormattedMessage
        id='Earn.r260601.Flipster_redemption_fee_redeem_faq_answer_narsil'
        values={{
          percentStandardRedemption: (
            <Formatted.Percent key='standardFee' value={details?.standardRedemptionFee} isRate toFixed precision={2} />
          ),
          percentInstantRedemption: (
            <Formatted.Percent key='instantFee' value={details?.instantRedemptionFee} isRate toFixed precision={2} />
          ),
          percentPerformance: (
            <Formatted.Percent key='performanceFee' value={details?.performanceFee} isRate toFixed precision={2} />
          ),
        }}
      />
    ),
  },
];

const narsilAdvantageItems: AdvantageItem[] = [
  {
    icon: <AutomationIconography size={48} />,
    title: <FormattedMessage id='Earn.r260224.Advantages_item_1_title' />,
    desc: <FormattedMessage id='Earn.r260224.Advantages_item_1_description' />,
  },
  {
    icon: <InterfaceIconography size={48} />,
    title: <FormattedMessage id='Earn.r260224.Advantages_item_2_title' />,
    desc: <FormattedMessage id='Earn.r260601.Advantages_item_2_description_narsil' />,
  },
  {
    icon: <SeamlessIconography size={48} />,
    title: <FormattedMessage id='Earn.r260224.Advantages_item_3_title' />,
    desc: <FormattedMessage id='Earn.r260601.Advantages_item_3_description_narsil' />,
  },
];

export const NRSUSDTPDetailsPage = () => {
  const { detailsAtom, sortedStrategiesAtom } = useBunja(narsilDetailsBunja);
  const { hasSubscribedBalanceAtom, overviewAtom, productCollateralRateAtom } = useBunja(narsilOverviewBunja);
  const openSubscribeModal = useNrsusdtpSubscribeModal();

  return (
    <ProductConfigProvider
      value={{
        detailsAtom,
        sortedStrategiesAtom,
        hasSubscribedBalanceAtom,
        productName: 'NRSUSDTP',
        currency: NrsusdtpCurrency,
        pricePrecision: nrsUsdtpPricePrecision,
        subscribeDeepLinkPath: 'dynamic-earn/subscribe/NRSUSDTP',
        strategyProviderPath: '/dynamic-earn/details/NRSUSDTP/narsil-labs',
        strategyProviderLink: <FormattedMessage id='Earn.r260601.Strategy_provider_link_narsil' />,
        overviewAtom,
        collateralRateAtom: productCollateralRateAtom,
        openSubscribeModal,
        advantageItems: narsilAdvantageItems,
        learnMorePath: '/blog/dynamic-earn-a-simpler-way-to-access-onchain-yield',
        getFaqItems: getNarsilFaqItems,
      }}
    >
      <DetailsPageLayout />
    </ProductConfigProvider>
  );
};
